import type {
  BerthPlanVisit,
  StepDirection,
} from '@/components/blocks/berth-plan/types';

/** Number of Bollards along the Quay. */
export const QUAY_BOLLARDS = 102;

/** Shift starts in local time: 05:00, 13:00 and 21:00. */
export const SHIFT_START_HOURS = [5, 13, 21];
/** Drag/keyboard time step. Tune on planner feedback. */
export const SNAP_MINUTES = 15;
export const PX_PER_HOUR = 12;
/** Zoom steps for the time axis; `PX_PER_HOUR` is the default. */
export const ZOOM_PX_PER_HOUR = [4, 6, 9, 12, 18] as const;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

export interface BerthBlock {
  visit: BerthPlanVisit;
  /** Raw inclusive bollard range, may extend past the Quay. */
  firstBollard: number;
  lastBollard: number;
  /** Bollard range clipped to 1..QUAY_BOLLARDS, used for drawing and conflicts. */
  drawFirst: number;
  drawLast: number;
  extendsBefore: boolean;
  extendsAfter: boolean;
  arrivalMs: number;
  departureMs: number;
  isLocked: boolean;
}

export interface BerthMove {
  bollardDelta: number;
  minutesDelta: number;
}

export interface BerthPlanLayout {
  /** Blocks on the drawn axis; history is left out while it is hidden. */
  blocks: Array<BerthBlock>;
  /** Visits with no bollards or no drawable times. */
  unplacedVisits: Array<BerthPlanVisit>;
  /** Blocks that departed before the cut-off day while history is hidden. */
  hiddenCount: number;
  conflictIds: Set<string>;
  startMs: number;
  endMs: number;
  dayStarts: Array<number>;
  shiftStarts: Array<number>;
  cutoffMs?: number;
}

/** A clamped move plus whether the Planning Cut-off stopped the time part. */
export interface ClampedMove {
  move: BerthMove;
  isCutoffBlocked: boolean;
}

export interface MovesLabel {
  key: 'movesBoth' | 'movesDischarge' | 'movesLoad';
  discharge: number;
  load: number;
}

/** Epoch ms of the Planning Cut-off, or `undefined` when there is none. */
export const parseCutoffMs = (
  lastTimeIn?: string | null
): number | undefined => {
  const ms = Date.parse(lastTimeIn ?? '');
  return Number.isNaN(ms) ? undefined : ms;
};

export const isBeforeCutoff = (iso: string, cutoffMs?: number): boolean =>
  cutoffMs !== undefined && Date.parse(iso) < cutoffMs;

/** A Locked Visit arrived before the Planning Cut-off. The `historic` flag is ignored. */
export const isLockedVisit = (
  visit: BerthPlanVisit,
  cutoffMs?: number
): boolean => isBeforeCutoff(visit.arrival_time, cutoffMs);

/** `null` for an Unplaced Visit or one whose times/bollards can't be drawn. */
export const toBerthBlock = (
  visit: BerthPlanVisit,
  cutoffMs?: number
): BerthBlock | null => {
  const { bollard_fore: fore, bollard_aft: aft } = visit;
  if (fore == null || aft == null) return null;
  const arrivalMs = Date.parse(visit.arrival_time);
  const departureMs = Date.parse(visit.departure_time ?? '');
  // Also rejects NaN on either side.
  if (!(departureMs > arrivalMs)) return null;

  const firstBollard = Math.min(fore, aft);
  const lastBollard = Math.max(fore, aft);
  const drawFirst = Math.max(1, firstBollard);
  const drawLast = Math.min(QUAY_BOLLARDS, lastBollard);
  // Entirely off the Quay: nothing to draw.
  if (drawFirst > drawLast) return null;

  return {
    visit,
    firstBollard,
    lastBollard,
    drawFirst,
    drawLast,
    extendsBefore: firstBollard < 1,
    extendsAfter: lastBollard > QUAY_BOLLARDS,
    arrivalMs,
    departureMs,
    isLocked: isLockedVisit(visit, cutoffMs),
  };
};

/**
 * Whether two visits share a bollard on the Quay. The overlap is measured on
 * the clipped ranges. A single shared bollard where one real range ends and
 * the other begins (an end-to-end handover) is not a conflict; the handover
 * is judged on the unclipped ranges.
 */
const sharesQuay = (first: BerthBlock, second: BerthBlock): boolean => {
  const from = Math.max(first.drawFirst, second.drawFirst);
  const to = Math.min(first.drawLast, second.drawLast);
  if (from > to) return false;
  if (from < to) return true;
  const isHandover = (ending: BerthBlock, starting: BerthBlock) =>
    ending.lastBollard === from &&
    starting.firstBollard === from &&
    ending.firstBollard < from;
  return !(isHandover(first, second) || isHandover(second, first));
};

/** Berth Conflicts: quay span and time window both overlap; touching edges don't count. */
export const findConflictIds = (
  blocks: ReadonlyArray<BerthBlock>
): Set<string> => {
  const ids = new Set<string>();
  blocks.forEach((first, index) => {
    blocks.slice(index + 1).forEach((second) => {
      const overlapsTime =
        first.arrivalMs < second.departureMs &&
        second.arrivalMs < first.departureMs;
      if (overlapsTime && sharesQuay(first, second)) {
        ids.add(first.visit.id);
        ids.add(second.visit.id);
      }
    });
  });
  return ids;
};

const startOfLocalDay = (ms: number): Date => {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date;
};

/** Local midnights covering [startMs, endMs), DST-safe. */
const getDayStarts = (startMs: number, endMs: number): Array<number> => {
  const days: Array<number> = [];
  for (
    const day = startOfLocalDay(startMs);
    day.getTime() < endMs;
    day.setDate(day.getDate() + 1)
  ) {
    days.push(day.getTime());
  }
  return days;
};

/** Local Shift starts within [startMs, endMs), DST-safe. */
const getShiftStarts = (
  dayStarts: ReadonlyArray<number>,
  startMs: number,
  endMs: number
): Array<number> =>
  dayStarts
    .flatMap((dayMs) =>
      SHIFT_START_HOURS.map((hour) => new Date(dayMs).setHours(hour, 0, 0, 0))
    )
    .filter((ms) => ms >= startMs && ms < endMs);

export const buildBerthPlanLayout = (
  visits: ReadonlyArray<BerthPlanVisit>,
  cutoffMs?: number,
  showHistory = true
): BerthPlanLayout | null => {
  const allBlocks = visits
    .map((visit) => toBerthBlock(visit, cutoffMs))
    .filter((block): block is BerthBlock => block !== null);
  if (allBlocks.length === 0) return null;
  const placedIds = new Set(allBlocks.map((block) => block.visit.id));

  // History: visits that left before the cut-off day; nothing there is editable.
  const historyEndMs =
    showHistory || cutoffMs === undefined
      ? undefined
      : startOfLocalDay(cutoffMs).getTime();
  const blocks =
    historyEndMs === undefined
      ? allBlocks
      : allBlocks.filter((block) => block.departureMs > historyEndMs);

  // The cut-off is always on the axis, even when no visit reaches it.
  const earliestMs =
    historyEndMs ??
    Math.min(
      ...blocks.map((block) => block.arrivalMs),
      ...(cutoffMs === undefined ? [] : [cutoffMs])
    );
  const latestMs = Math.max(
    ...blocks.map((block) => block.departureMs),
    ...(cutoffMs === undefined ? [] : [cutoffMs])
  );
  const startMs = startOfLocalDay(earliestMs).getTime();
  const lastDay = startOfLocalDay(latestMs);
  lastDay.setDate(lastDay.getDate() + 1);
  const endMs = lastDay.getTime();
  const dayStarts = getDayStarts(startMs, endMs);

  return {
    blocks,
    unplacedVisits: visits.filter((visit) => !placedIds.has(visit.id)),
    hiddenCount: allBlocks.length - blocks.length,
    conflictIds: findConflictIds(allBlocks),
    startMs,
    endMs,
    dayStarts,
    shiftStarts: getShiftStarts(dayStarts, startMs, endMs),
    cutoffMs,
  };
};

export const msToPx = (
  ms: number,
  startMs: number,
  pxPerHour: number = PX_PER_HOUR
): number => ((ms - startMs) * pxPerHour) / HOUR_MS;

/** Duration in ms covered by `px` on the time axis. */
export const pxToMs = (px: number, pxPerHour: number = PX_PER_HOUR): number =>
  (px * HOUR_MS) / pxPerHour;

/**
 * Percent from the right edge of the Quay for a position in bollard units,
 * where 1 is the right edge of Bollard 1 (Bollards descend left to right).
 * Bollard n's centre is `bollardToPct(n + 0.5)`.
 */
export const bollardToPct = (position: number): number =>
  ((position - 1) / QUAY_BOLLARDS) * 100;

/**
 * Where a block sits along the Quay, in percent from the Bollard 1 (right)
 * edge. It spans from its first bollard's centre to its last one's, so an
 * end-to-end handover meets on the shared bollard instead of overlapping. A
 * side that runs past the Quay reaches the Quay edge.
 */
export const getBlockSpanPct = (
  block: BerthBlock
): { startPct: number; widthPct: number } => {
  // A single-bollard visit has no centre-to-centre span, so it gets a sliver
  // around its bollard centre; it overlaps a handover neighbour only slightly.
  const inset = block.drawFirst === block.drawLast ? 0.25 : 0.5;
  const startPct = block.extendsBefore
    ? 0
    : bollardToPct(block.drawFirst + inset);
  const endPct = block.extendsAfter
    ? 100
    : bollardToPct(block.drawLast + 1 - inset);
  return { startPct, widthPct: endPct - startPct };
};

/**
 * Snaps a screen pointer delta to whole bollards and `SNAP_MINUTES` steps.
 * Bollards descend left to right and time runs down, so right is towards
 * Bollard 1 and down is later. With
 * `arrivalMs`, a time move lands the arrival on the `SNAP_MINUTES` grid, so an
 * off-grid arrival doesn't stay off-grid.
 */
export const pixelDeltaToMove = (
  dxPx: number,
  dyPx: number,
  pxPerBollard: number,
  pxPerHour: number = PX_PER_HOUR,
  arrivalMs?: number
): BerthMove => {
  const minutes = (dyPx * 60) / pxPerHour;
  const steps = Math.round(minutes / SNAP_MINUTES);
  const snapMs = SNAP_MINUTES * MINUTE_MS;
  const minutesDelta =
    steps === 0 || arrivalMs === undefined
      ? steps * SNAP_MINUTES
      : (Math.round((arrivalMs + minutes * MINUTE_MS) / snapMs) * snapMs -
          arrivalMs) /
        MINUTE_MS;
  // `|| 0` keeps a still pointer from yielding -0.
  return { bollardDelta: Math.round(-dxPx / pxPerBollard) || 0, minutesDelta };
};

/**
 * Keeps the block on the Quay. A visit already partly off the Quay can only
 * move inwards. Time never moves the arrival before the Planning Cut-off.
 */
export const clampMove = (
  block: BerthBlock,
  move: BerthMove,
  cutoffMs?: number
): ClampedMove => {
  const minBollardDelta = block.firstBollard < 1 ? 0 : 1 - block.firstBollard;
  const maxBollardDelta =
    block.lastBollard > QUAY_BOLLARDS ? 0 : QUAY_BOLLARDS - block.lastBollard;
  const minMinutes =
    cutoffMs === undefined
      ? -Infinity
      : Math.ceil((cutoffMs - block.arrivalMs) / MINUTE_MS);

  return {
    move: {
      bollardDelta: Math.min(
        Math.max(move.bollardDelta, minBollardDelta),
        maxBollardDelta
      ),
      minutesDelta: Math.max(move.minutesDelta, minMinutes),
    },
    isCutoffBlocked: move.minutesDelta < minMinutes,
  };
};

const shiftTime = <T extends string | null | undefined>(
  iso: T,
  ms: number
): T | string => (iso ? new Date(Date.parse(iso) + ms).toISOString() : iso);

/** Moving a visit shifts both bollards and all four times, keeping span and durations. */
export const moveShipVisit = (
  visit: BerthPlanVisit,
  { bollardDelta, minutesDelta }: BerthMove
): BerthPlanVisit => {
  const ms = minutesDelta * MINUTE_MS;
  return {
    ...visit,
    bollard_fore: (visit.bollard_fore ?? 0) + bollardDelta,
    bollard_aft: (visit.bollard_aft ?? 0) + bollardDelta,
    arrival_time: shiftTime(visit.arrival_time, ms),
    departure_time: shiftTime(visit.departure_time, ms),
    start_work_time: shiftTime(visit.start_work_time, ms),
    end_work_time: shiftTime(visit.end_work_time, ms),
  };
};

/**
 * Editing the arrival shifts departure and the work window by the same
 * amount, so the visit keeps its durations. Empty times stay empty.
 */
export const rescheduleShipVisit = (
  visit: BerthPlanVisit,
  arrivalIso: string
): Pick<
  BerthPlanVisit,
  'arrival_time' | 'departure_time' | 'start_work_time' | 'end_work_time'
> => {
  const ms = Date.parse(arrivalIso) - Date.parse(visit.arrival_time);
  if (!Number.isFinite(ms)) return { arrival_time: arrivalIso };
  return {
    arrival_time: arrivalIso,
    departure_time: shiftTime(visit.departure_time, ms),
    start_work_time: shiftTime(visit.start_work_time, ms),
    end_work_time: shiftTime(visit.end_work_time, ms),
  };
};

export const isZeroMove = ({ bollardDelta, minutesDelta }: BerthMove) =>
  bollardDelta === 0 && minutesDelta === 0;

/** Opens at the Planning Cut-off, else at the earliest visit. */
export const getInitialScrollMs = (layout: BerthPlanLayout): number =>
  layout.cutoffMs ?? Math.min(...layout.blocks.map((block) => block.arrivalMs));

/** Whether a departure is after the arrival; an empty or invalid time fails. */
export const isDepartureAfterArrival = (
  arrivalIso: string,
  departureIso: string
): boolean => Date.parse(departureIso) > Date.parse(arrivalIso);

/** Discharge/load counts to show on a block; hidden parts are null/0. */
export const getMovesLabel = (visit: BerthPlanVisit): MovesLabel | null => {
  const discharge = visit.n_moves_discharge ?? 0;
  const load = visit.n_moves_load ?? 0;
  const hasDischarge = discharge > 0;
  const hasLoad = load > 0;
  if (hasDischarge && hasLoad) return { key: 'movesBoth', discharge, load };
  if (hasDischarge) return { key: 'movesDischarge', discharge, load };
  if (hasLoad) return { key: 'movesLoad', discharge, load };
  return null;
};

/** Visits matching `query` on ship, service or visit id; `null` when there is no query. */
export const findMatchingVisitIds = (
  visits: ReadonlyArray<BerthPlanVisit>,
  query: string
): Set<string> | null => {
  const needle = query.trim().toLowerCase();
  if (!needle) return null;
  return new Set(
    visits
      .filter((visit) =>
        [visit.ship_id, visit.shipping_line_id, visit.id].some((field) =>
          field.toLowerCase().includes(needle)
        )
      )
      .map((visit) => visit.id)
  );
};

/** Ids of drawn blocks in `ids`, earliest arrival first, for stepping through them. */
export const getBlockSequence = (
  blocks: ReadonlyArray<BerthBlock>,
  ids: ReadonlySet<string>
): Array<string> =>
  blocks
    .filter((block) => ids.has(block.visit.id))
    .toSorted((first, second) => first.arrivalMs - second.arrivalMs)
    .map((block) => block.visit.id);

/** The id after (or before) `currentId`, wrapping; the first one when `currentId` is not in the list. */
export const stepThrough = (
  ids: ReadonlyArray<string>,
  currentId: string | null,
  direction: StepDirection
): string | undefined => {
  if (ids.length === 0) return undefined;
  const index = currentId === null ? -1 : ids.indexOf(currentId);
  if (index === -1) return direction === 1 ? ids.at(0) : ids.at(-1);
  return ids.at((index + direction + ids.length) % ids.length);
};

/** Compact date and 24h time for a block, e.g. "24 Oct, 06:00"; empty when invalid. */
export const formatBlockTime = (
  iso: string | null | undefined,
  locale: string
): string => {
  const ms = Date.parse(iso ?? '');
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
};
